import React, { useEffect, useState } from 'react';
import { X, Loader2, MapPin } from 'lucide-react';
import LocationSelectionModal from './LocationSelectionModal';
import { simulationService } from '../../services/simulationService';

const toDateInputValue = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const EditProjectModal = ({ isOpen, onClose, projectId, onSaved }) => {
  const [formData, setFormData] = useState({
    projectName: '',
    location: null,
    startDate: '',
    endDate: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!isOpen || !projectId) return;
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      setLoadError('');
      try {
        const response = await simulationService.getProjectMetadata(projectId);
        if (cancelled) return;
        if (response.success && response.data) {
          const meta = response.data;
          setFormData({
            projectName: meta.projectName || projectId,
            location: meta.location && meta.location.lat && meta.location.lng
              ? {
                  lat: meta.location.lat,
                  lng: meta.location.lng,
                  address: meta.location.address || '',
                  coordinates: { lat: meta.location.lat, lng: meta.location.lng }
                }
              : null,
            startDate: toDateInputValue(meta.startDate),
            endDate: toDateInputValue(meta.endDate)
          });
        } else {
          // No metadata yet — fall back to projectId only
          setFormData({
            projectName: projectId,
            location: null,
            startDate: '',
            endDate: ''
          });
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError('Projektdaten konnten nicht geladen werden.');
          setFormData({
            projectName: projectId,
            location: null,
            startDate: '',
            endDate: ''
          });
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, projectId]);

  const validate = () => {
    const next = {};
    const name = formData.projectName.trim();
    if (!name) {
      next.projectName = 'Projektname ist erforderlich';
    } else if (name.length < 2) {
      next.projectName = 'Projektname muss mindestens 2 Zeichen lang sein';
    } else if (!/^[a-zA-Z0-9\s\-_äöüÄÖÜß]+$/.test(name)) {
      next.projectName = 'Projektname enthält ungültige Zeichen';
    }

    if (formData.location && (!formData.location.lat || !formData.location.lng)) {
      next.location = 'Standort ist ungültig';
    }

    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      if (end <= start) {
        next.endDate = 'Enddatum muss nach dem Startdatum liegen';
      }
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleLocationSelect = (location) => {
    setFormData(prev => ({ ...prev, location }));
    if (errors.location) {
      setErrors(prev => ({ ...prev, location: '' }));
    }
  };

  const handleSave = async () => {
    if (!validate()) return;

    const updates = {};
    const trimmedName = formData.projectName.trim();
    if (trimmedName !== projectId) {
      updates.newProjectName = trimmedName;
    }
    if (formData.location && formData.location.lat && formData.location.lng) {
      updates.location = {
        lat: formData.location.lat,
        lng: formData.location.lng,
        address: formData.location.address
      };
    }
    updates.startDate = formData.startDate || null;
    updates.endDate = formData.endDate || null;

    setIsSaving(true);
    try {
      const response = await simulationService.updateProject(projectId, updates);
      if (response && response.success) {
        const finalName = response.data?.projectName || trimmedName;
        if (onSaved) onSaved({ previousId: projectId, newId: finalName });
        onClose();
      } else {
        setErrors(prev => ({
          ...prev,
          form: response?.error || 'Fehler beim Speichern.'
        }));
      }
    } catch (err) {
      setErrors(prev => ({
        ...prev,
        form: err.message || 'Fehler beim Speichern.'
      }));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (isSaving) return;
    setErrors({});
    setLocationModalOpen(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-8 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Projekt bearbeiten</h2>
          <button
            onClick={handleCancel}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            disabled={isSaving}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600 mb-2" />
            <p className="text-gray-700">Projektdaten werden geladen...</p>
          </div>
        ) : (
          <>
            {loadError && (
              <div className="mb-4 p-3 bg-yellow-50 text-yellow-700 rounded">
                {loadError}
              </div>
            )}

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Projektname <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.projectName}
                  onChange={(e) => handleInputChange('projectName', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.projectName ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  disabled={isSaving}
                />
                {errors.projectName && (
                  <p className="text-red-500 text-sm mt-1">{errors.projectName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Standort
                </label>
                <div
                  onClick={() => !isSaving && setLocationModalOpen(true)}
                  className={`w-full px-3 py-2 border rounded-md cursor-pointer transition-colors hover:bg-gray-50 flex items-center gap-2 ${
                    errors.location ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  } ${isSaving ? 'cursor-not-allowed opacity-50' : ''}`}
                >
                  <MapPin className="h-4 w-4 text-gray-400" />
                  <span className={formData.location ? 'text-gray-900' : 'text-gray-500'}>
                    {formData.location && formData.location.address
                      ? formData.location.address
                      : 'Standort auf Karte auswählen...'}
                  </span>
                </div>
                {errors.location && (
                  <p className="text-red-500 text-sm mt-1">{errors.location}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Projektzeitraum Start
                  </label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => handleInputChange('startDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={isSaving}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Projektzeitraum Ende
                  </label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => handleInputChange('endDate', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.endDate ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    disabled={isSaving}
                  />
                  {errors.endDate && (
                    <p className="text-red-500 text-sm mt-1">{errors.endDate}</p>
                  )}
                </div>
              </div>

              {errors.form && (
                <div className="p-3 bg-red-50 text-red-600 rounded">
                  {errors.form}
                </div>
              )}
            </div>

            {isSaving ? (
              <div className="flex flex-col items-center justify-center py-4 mb-6">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600 mb-2" />
                <p className="text-gray-700">Projekt wird gespeichert...</p>
              </div>
            ) : (
              <div className="flex justify-end gap-4">
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Speichern
                </button>
              </div>
            )}
          </>
        )}

        <LocationSelectionModal
          isOpen={locationModalOpen}
          onClose={() => setLocationModalOpen(false)}
          onSave={handleLocationSelect}
          initialLocation={formData.location}
        />
      </div>
    </div>
  );
};

export default EditProjectModal;
